import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { AssuredPersonRelationship, AssuredPersonRequest, formatAssuredPersonRelationship } from "@/domain/assured/Assured"
import { assuredPeopleSchema } from "@/domain/assured/AssuredSchemas"
import { Offering, ServiceType } from "@/domain/offerings/offering"
import { DocType } from "@/domain/shared"
import { useClient } from "@/hooks/api/useClients"
import { calculateAge, calculateAgeStr } from "@/utils/dates"
import { formatDocNumber } from "@/utils/helpers"
import { zodResolver } from "@hookform/resolvers/zod"
import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import z from "zod"
import DateSelector from "../../shared/DateSelector"
import { formatDate } from "date-fns"

interface AssuredPeopleDataFormProps {
  offering?: Offering
  userId?: string
  onSubmit: (data: AssuredPersonRequest[]) => void
  onBack: () => void
  existingData: AssuredPersonRequest[]
}

export const AssuredPeopleDataForm = ({ offering, onSubmit, onBack, existingData, userId }: AssuredPeopleDataFormProps) => {
  const { data: client } = useClient(userId || '')
  const [peopleList, setPeopleList] = useState<AssuredPersonRequest[]>(existingData || [])
  const [error, setError] = useState<string>('')
  const serviceType = offering?.serviceType
  const schema = assuredPeopleSchema();
  type FormValues = z.infer<typeof schema>

  const MAX_PEOPLE = 4
  const isAbonoWithMultiplePeople = serviceType === ServiceType.ABONOS && offering?.hasMultipleOffered
  const maxPeopleReached = (isAbonoWithMultiplePeople && peopleList.length >= MAX_PEOPLE)
    || (!isAbonoWithMultiplePeople && peopleList.length === 1)

  const defaultFormValues: AssuredPersonRequest = {
    name: '',
    docType: DocType.DNI,
    docNumber: '',
    dateOfBirth: undefined,
    relationship: AssuredPersonRelationship.TITULAR
  }

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: defaultFormValues
  })

  useEffect(() => {
    if (existingData.length > 0 && peopleList.length === 0) {
      setPeopleList(existingData)
      if (!isAbonoWithMultiplePeople && existingData.length === 1) {
        form.reset(existingData[0])
      }
    }
  }, [existingData, isAbonoWithMultiplePeople, form])

  const dateOfBirth = form.watch('dateOfBirth')

  const handleDocNumberChange = (value: string) => {
    const docType = form.getValues("docType") as DocType || undefined
    form.setValue('docNumber', formatDocNumber(value, docType))
  }

  const handleCopyClientData = () => {
    if (!client?.userData) return
    form.setValue('name', client.userData.legalName || '')
    form.setValue('docType', client.userData.docType || DocType.DNI)
    form.setValue('docNumber', formatDocNumber(client.userData.docNumber || '', client.userData.docType || undefined))
    form.setValue('dateOfBirth', client.userData.dateOfBirth as Date)
    form.setValue('relationship', AssuredPersonRelationship.TITULAR)
  }

  const validatePersonRules = (person: AssuredPersonRequest, currentList: AssuredPersonRequest[]): string | null => {
    const activePeople = currentList;
    const newRelationship = person.relationship;

    if (newRelationship === AssuredPersonRelationship.HIJO) {
      if (person.dateOfBirth) {
        const age = calculateAge(new Date(person.dateOfBirth));
        if (age >= 18) {
          return 'Los hijos de un abono familiar deben ser menores de edad';
        }
      }
      const hijosCount = activePeople.filter(p => p.relationship === AssuredPersonRelationship.HIJO).length;
      if (hijosCount >= 2) {
        return 'No es posible agregar más de 2 hijos por abono familiar';
      }
    } else if (newRelationship === AssuredPersonRelationship.TITULAR) {
      const titularCount = activePeople.filter(p => p.relationship === AssuredPersonRelationship.TITULAR).length;
      if (titularCount >= 1) {
        return 'No es posible agregar más de 1 titular por abono familiar';
      }
    } else if (newRelationship === AssuredPersonRelationship.CONYUGE) {
      const conyugeCount = activePeople.filter(p => p.relationship === AssuredPersonRelationship.CONYUGE).length;
      if (conyugeCount >= 1) {
        return 'No es posible agregar más de 1 cónyuge por abono familiar';
      }
    }

    // Check for duplicate document numbers
    const isDuplicateDoc = activePeople.some(p => p.docNumber === person.docNumber);
    if (isDuplicateDoc) {
      return 'Ya existe una persona con el mismo número de documento en este grupo';
    }

    return null;
  }

  const validateAndAddPerson = () => {
    setError('')

    form.trigger().then((isValid) => {
      if (!isValid) {
        setError('Por favor complete todos los campos requeridos correctamente')
        return
      }

      if (isAbonoWithMultiplePeople && peopleList.length >= MAX_PEOPLE) {
        setError(`No se pueden agregar más de ${MAX_PEOPLE} personas`)
        return
      }

      const formData = form.getValues()
      const newPerson: AssuredPersonRequest = { ...formData }

      if (isAbonoWithMultiplePeople) {
        const ruleError = validatePersonRules(newPerson, peopleList);
        if (ruleError) {
          setError(ruleError);
          return;
        }
      }

      const updatedList = [...peopleList, newPerson]
      setPeopleList(updatedList)
      form.reset(defaultFormValues)
    })
  }

  const removePerson = (index: number) => {
    setPeopleList(peopleList.filter((_, i) => i !== index))
    form.reset(defaultFormValues)
    setError('')
  }

  const editPerson = (index: number) => {
    const person = peopleList[index]
    form.reset({
      name: person.name,
      docType: person.docType,
      docNumber: person.docNumber,
      dateOfBirth: person.dateOfBirth,
      relationship: person.relationship,
    })
    setPeopleList(peopleList.filter((_, i) => i !== index))
    setError('')
  }

  const handleReset = () => {
    form.reset(defaultFormValues)
    setError('')
  }

  const handleFormSubmit = () => {
    if (isAbonoWithMultiplePeople) {
      if (peopleList.length === 0) {
        setError('Debe agregar al menos una persona')
        return
      }
      onSubmit(peopleList)
    }
    else {
      if (peopleList.length === 1 && !form.formState.isDirty) {
        onSubmit(peopleList)
        return
      }

      form.trigger().then((isValid) => {
        if (!isValid) {
          setError('Por favor complete todos los campos requeridos correctamente')
          return
        }

        const person = { ...form.getValues() };
        const ruleError = validatePersonRules(person, []);
        if (ruleError) {
          setError(ruleError);
          return;
        }

        onSubmit([person])
      })
    }
  }

  const nextButtonDisabled = form.formState.isSubmitting
    || (isAbonoWithMultiplePeople && peopleList.length < 2)
  const canAddMorePeople = !maxPeopleReached

  return (
    <div className="space-y-4">
      <div className="flex justify-between">
        <h3 className="text-lg font-medium flex flex-col">
          <span>{offering!.name}</span>
          <span className="text-sm font-mono text-muted-foreground">{" "}(Código {offering!.code})</span>
        </h3>
        {client && (
          <Button
            type="button"
            variant="outline"
            onClick={handleCopyClientData}
            className="text-sm"
          >
            Copiar datos del cliente
          </Button>
        )}
      </div>

      {canAddMorePeople &&
        <div className="bg-white p-6 rounded-lg border shadow-sm">
          <div className="space-y-4">
            <h3 className="text-lg font-medium">
              Datos del Abonado
            </h3>

            <div className="space-y-2">
              <label htmlFor="name" className="text-sm font-medium">Apellido, Nombre</label>
              <input
                type="text"
                className={`w-full p-2 border rounded-md ${form.formState.errors.name ? 'border-red-500' : ''}`}
                value={form.watch('name') || ''}
                onChange={(e) => form.setValue('name', e.target.value)}
              />
              {form.formState.errors.name && (
                <p className="text-red-500 text-sm mt-1">
                  {form.formState.errors.name.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="docType" className="text-sm font-medium">Tipo de documento</label>
              <Select
                onValueChange={(value) => form.setValue('docType', value as DocType)}
                value={form.watch('docType') || ''}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar tipo de documento" />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(DocType).map((docType) => (
                    <SelectItem key={docType} value={docType}>
                      {docType}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.formState.errors.docType && (
                <p className="text-red-500 text-sm mt-1">
                  {form.formState.errors.docType.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="docNumber" className="text-sm font-medium">Número de documento</label>
              <input
                type="text"
                placeholder="12.345.678"
                className={`w-full p-2 border rounded-md ${form.formState.errors.docNumber ? 'border-red-500' : ''}`}
                value={form.watch('docNumber') || ''}
                onChange={(e) => handleDocNumberChange(e.target.value)}
              />
              {form.formState.errors.docNumber && (
                <p className="text-red-500 text-sm mt-1">
                  {form.formState.errors.docNumber.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="relationship" className="text-sm font-medium">Parentesco con el titular</label>
              <Select
                onValueChange={(value) => form.setValue('relationship', value as AssuredPersonRelationship)}
                value={form.watch('relationship') || ''}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar parentesco" />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(AssuredPersonRelationship)
                    .filter(rel => (offering?.hasMultipleOffered) || (!offering?.hasMultipleOffered && rel === AssuredPersonRelationship.TITULAR))
                    .map((rel) => (
                      <SelectItem key={rel} value={rel}>
                        {formatAssuredPersonRelationship(rel)}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {form.formState.errors.relationship && (
                <p className="text-red-500 text-sm mt-1">
                  {form.formState.errors.relationship.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="dateOfBirth" className="text-sm font-medium">Fecha de nacimiento</label>
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <DateSelector
                    key={`birth-${dateOfBirth instanceof Date ? dateOfBirth.getTime() : 'empty'}`}
                    initialDate={dateOfBirth instanceof Date ? dateOfBirth : undefined}
                    onDateSelected={(date: Date) => {
                      form.setValue('dateOfBirth', date)
                    }}
                  />
                </div>
                {dateOfBirth && (
                  <span className="text-md font-mono text-gray-600 whitespace-nowrap">
                    ({calculateAgeStr(new Date(dateOfBirth))})
                  </span>
                )}
              </div>
              {form.formState.errors.dateOfBirth && (
                <p className="text-red-500 text-sm mt-1">
                  {form.formState.errors.dateOfBirth.message}
                </p>
              )}
            </div>

            {isAbonoWithMultiplePeople && (
              <div className="flex justify-between">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleReset}
                  className="text-sm"
                >
                  Limpiar campos
                </Button>
                <Button
                  type="button"
                  variant="info"
                  onClick={validateAndAddPerson}
                  className="text-sm"
                >
                  Agregar persona ({peopleList.length + 1}/{MAX_PEOPLE})
                </Button>
              </div>
            )}
          </div>
        </div>}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {peopleList.length > 0 && (
        <div className="space-y-3">
          {isAbonoWithMultiplePeople && <h3 className="text-lg font-medium">Datos de los abonados ({peopleList.length}/{MAX_PEOPLE})</h3>}
          {!isAbonoWithMultiplePeople && <h3 className="text-lg font-medium">Datos del abonado</h3>}
          {peopleList.map((person, index) => (
            <div key={index} className="bg-gray-50 p-4 rounded-lg border">
              <div className="flex justify-between items-start">
                <div className="space-y-1">
                  <p className="font-medium">{person.name}</p>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Documento: </span>
                    {person.docType} {person.docNumber}
                  </p>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Fecha de nacimiento: </span>
                    {person.dateOfBirth && `${formatDate(person.dateOfBirth, 'dd/MM/yyyy')} (${calculateAgeStr(new Date(person.dateOfBirth))})`}
                  </p>
                  {person.relationship && (
                    <p className="text-sm text-gray-500 italic">
                      <span className="font-medium">Parentesco: </span>
                      {formatAssuredPersonRelationship(person.relationship)}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => editPerson(index)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    Editar
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => removePerson(index)}
                    className="text-red-600 hover:text-red-800"
                  >
                    Eliminar
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="flex justify-between pt-4">
        <Button type="button" variant="outline" onClick={onBack}>
          Atrás
        </Button>
        <div className="space-x-2">
          <Button type="button" onClick={handleFormSubmit} disabled={nextButtonDisabled}>
            Continuar
          </Button>
        </div>
      </div>
    </div>
  )
}
